import type { Editor } from '@tiptap/react';

import { increaseListIndent, outdentListItem } from '../utils/listItemUtils';

/**
 * Handle Tab key indentation for lists with 5-level depth limit
 */
export const handleListTabIndentation = (editor: Editor, event: KeyboardEvent): boolean => {
  if (event.key !== 'Tab') {
    return false;
  }

  const isInList = editor.isActive('bulletList') || editor.isActive('orderedList');
  if (!isInList) {
    return false;
  }

  event.preventDefault();
  const { state, view } = editor;
  const { dispatch } = view;

  if (event.shiftKey) {
    outdentListItem(state, dispatch);
  } else {
    increaseListIndent(state, dispatch);
  }

  return true;
};

/**
 * Handle backspace key for lists - EXACT logic from original TiptapEditor.tsx
 * This handles empty list items by lifting them out of the list
 */
export const handleListBackspace = (editor: Editor, event: KeyboardEvent): boolean => {
  if (event.key !== 'Backspace') {
    return false;
  }

  const { state } = editor;

  if (
    state.selection.empty &&
    (editor?.isActive('bulletList') || editor?.isActive('orderedList'))
  ) {
    const { $from } = state.selection;
    const parentNode = $from.node($from.depth);

    if (parentNode.type.name === 'paragraph' && parentNode.content.size === 0) {
      event.preventDefault();
      editor.commands.liftListItem('listItem');
      return true;
    }
  }

  return false;
};

/**
 * Enhanced bullet list button handler with depth limitation
 */
export const handleBulletListToggle = (editor: Editor, refreshActiveState: () => void): void => {
  editor.view.dom.focus({ preventScroll: true });
  editor.chain().toggleBulletList().run();
  refreshActiveState();
};

/**
 * Enhanced ordered list button handler with depth limitation
 */
export const handleOrderedListToggle = (editor: Editor, refreshActiveState: () => void): void => {
  editor.view.dom.focus({ preventScroll: true });
  editor.chain().toggleOrderedList().run();
  refreshActiveState();
};

/**
 * Check if current list depth exceeds maximum allowed (5 levels)
 */
export const isListDepthAtMax = (editor: Editor): boolean => {
  if (!editor.isActive('bulletList') && !editor.isActive('orderedList')) {
    return false;
  }

  const { state } = editor;
  const { $from } = state.selection;

  const depth = Array.from({ length: $from.depth }).reduce((acc: number, _, i: number) => {
    const index = $from.depth - i - 1;
    return $from.node(index).type.name === 'listItem' ? acc + 1 : acc;
  }, 0);

  return depth >= 5;
};
